import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdMenu,
  MdNewspaper,
  MdOutlineFactCheck,
  MdOutlineFeaturedPlayList,
  MdOutlineFullscreen,
  MdOutlineFullscreenExit,
} from "react-icons/md";
import { NavLink } from "react-router-dom";

import Logo from "../../assets/blue_logo.svg";
import { ReactComponent as TeachersIcon } from "../../assets/Group318.svg";
import { ReactComponent as StudentsIcon } from "../../assets/Group320.svg";
import { ReactComponent as ClassroomIcon } from "../../assets/Group322.svg";
import { ReactComponent as HomeIcon } from "../../assets/Group324.svg";
import { ReactComponent as GeneralReportsIcon } from "../../assets/Group326.svg";
import { ReactComponent as AdaptedIcon } from "../../assets/Group330.svg";
import { ReactComponent as HellocodeIcon } from "../../assets/Group331.svg";
import { ReactComponent as Top5Icon } from "../../assets/Group335.svg";
import { ReactComponent as Top10Icon } from "../../assets/Group337.svg";
import { ReactComponent as ImportStudentsIcon } from "../../assets/importStudents.svg";
import { ReactComponent as LogoutIcon } from "../../assets/logout_icon.svg";
import { ReactComponent as ContactIcon } from "../../assets/Path103.svg";
import { ReactComponent as ClassroomReportsIcon } from "../../assets/Subtraction3.svg";
import user_placeholder from "../../assets/user-profile.jpg";
import { USER_TYPE } from "../../constants/common";
import useAuth from "../../hooks/useAuth";
import { getLanguage } from "../../utils/getLanguage";
import Button from "../Button";
import LanguageSwitcher from "../LanguageSwitcher";
import NotificationDropdown from "../NotificationDropdown";
import ThemeToggle from "../ThemeToggle";

const SchoolHeader = ({
  toggleSidebar,
  toggleLayout,
  layout,
  openContactAccountManagerModal,
  handleSignOut,
  closeSidebar,
}) => {
  const user = useAuth();
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const lang = getLanguage();
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error(`Error exiting fullscreen: ${err.message}`);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        document.exitFullscreen().catch((err) => {
          console.error(`Error exiting fullscreen: ${err.message}`);
        });
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const navItems = [
    {
      name: "teachers",
      icon: <TeachersIcon className="h-6 w-6" />,
      path: "teachers",
    },
    {
      name: "classrooms",
      icon: <ClassroomIcon className="h-6 w-6" />,
      path: "classrooms",
    },
    {
      name: "students",
      icon: <StudentsIcon className="h-6 w-6" />,
      path: "students",
    },
    {
      name: "import_students",
      icon: <ImportStudentsIcon className="h-6 w-6" />,
      path: "import-students",
    },
    {
      name: "general_reports",
      icon: <GeneralReportsIcon className="h-6 w-6" />,
      path: "general-reports",
    },
    {
      name: "classroom_reports",
      icon: <ClassroomReportsIcon className="h-6 w-6" />,
      path: "classroom-reports",
    },
    {
      name: "adapted_courses",
      icon: <AdaptedIcon className="h-6 w-6" />,
      path: "adapted-courses",
    },
    {
      name: "top_weekly_5_students",
      icon: <Top5Icon className="h-6 w-6" />,
      path: "top-weekly-5-students",
    },
    {
      name: "top_10_students",
      icon: <Top10Icon className="h-6 w-6" />,
      path: "top-10-students",
    },
    {
      name: "contact_account_manager",
      icon: <ContactIcon className="h-6 w-6" />,
      path: "button",
      onClick: openContactAccountManagerModal,
    },
    {
      name: "logout",
      icon: <LogoutIcon className="h-6 w-6" />,
      path: "button",
      onClick: handleSignOut,
    },
    {
      name: "",
      icon: "",
      path: "placeholder",
      onClick: () => {},
    },
  ];

  return (
    <header className="items-center bg-white py-3 text-text shadow-md dark:bg-dark-surface dark:text-dark-text dark:shadow-none">
      <div className="container-fluid mx-auto flex items-center justify-between px-4 sm:px-6">
        <div className="flex flex-row items-center space-x-4">
          {layout == "dense" && (
            <Button
              variant="text"
              color="white"
              icon={<MdMenu size={26} />}
              onClick={toggleSidebar}
              aria-label="Toggle Sidebar"
              className="group relative flex h-10 w-10 items-center justify-center"
            />
          )}
          {layout === "centered" && (
            <div className="flex items-center space-x-4">
              <NavLink to={`/${lang}/${USER_TYPE.SCHOOL.ROLE}`}>
                <img src={Logo} alt="School" />
              </NavLink>
              <h1 className={`mx-4 text-xs text-gray-400 dark:text-white`}>
                <span className="max-w-full truncate font-semibold text-text dark:text-white">
                  {user?.school?.name}
                </span>
                <br />
                {USER_TYPE.SCHOOL.LABEL} Admin Dashboard
              </h1>
            </div>
          )}

          {layout === "centered" && (
            <div className="flex space-x-4">
              {Array.from({ length: Math.ceil(navItems.length / 4) }).map((_, i) => (
                <Menu key={i} width={36} buttonContent={<h1>{t(`menu_${i + 1}`)}</h1>}>
                  {navItems.slice(i * 4, i * 4 + 4).map((item, index) => (
                    <MenuItem key={index}>
                      {item.path === "button" ? (
                        <button onClick={item.onClick}>{t(item.name)}</button>
                      ) : (
                        <NavLink
                          to={`/${lang}/${USER_TYPE.SCHOOL.ROLE}/${item.path}`}
                          className={({ isActive }) => (isActive ? "text-blue-500" : "text-gray-400")}
                          onClick={closeSidebar}
                        >
                          {t(item.name)}
                        </NavLink>
                      )}
                    </MenuItem>
                  ))}
                </Menu>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1">
          {/* <button onClick={toggleLayout} aria-label="Toggle Fullscreen">
            {layout == "centered" ? (
              <BiExitFullscreen className="h-5 w-5" />
            ) : (
              <BiFullscreen className="h-5 w-5" />
            )}
          </button> */}
          <Button
            onClick={toggleFullscreen}
            icon={isFullscreen ? <MdOutlineFullscreenExit size={20} /> : <MdOutlineFullscreen size={20} />}
            variant="text"
            color="white"
          />
          <ThemeToggle />
          <LanguageSwitcher />
          <NotificationDropdown />
          <Menu
            menuButton={
              <MenuButton>
                <div className="flex items-center space-x-2 rounded-lg px-2 capitalize">
                  {user.img_uri ? (
                    <img
                      src={user.img_uri}
                      alt="User Avatar"
                      className="h-8 w-8 rounded-full border border-gray-400 object-cover"
                    />
                  ) : (
                    <img
                      src={user_placeholder}
                      alt="User Avatar"
                      className="h-8 w-8 rounded-full border border-gray-400 object-cover"
                    />
                  )}
                  <span
                    title={user.username || "UserName"}
                    className="max-w-[22ch] truncate text-xs font-semibold text-[#535353] dark:text-white"
                  >
                    {user.username || "UserName"}
                  </span>
                </div>
              </MenuButton>
            }
            transition
            align="end"
            menuClassName="rounded-lg p-1 bg-white dark:border-gray-700 dark:bg-[#2C3A4A]"
          >
            <a target="_blank" rel="noopener noreferrer">
              <MenuItem className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs">
                <MdNewspaper size={20} className="" />
                {t("teacher_certificate")}
              </MenuItem>
            </a>

            {/* <a
              href={
                lang == "ar"
                  ? "/static/School_Admin_User_Manual_ar.pdf"
                  : "/static/School_Admin_User_Manual.pdf"
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <MenuItem className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs">
                <MdOutlineFeaturedPlayList size={20} className="" />
                {t("user_manual")}
              </MenuItem>
            </a>
            <a
              href={
                lang == "ar" ? "/static/Assessment_Guidelines_ar.pdf" : "/static/Assessment_Guidelines.pdf"
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <MenuItem className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs">
                <MdOutlineFactCheck size={20} className="" />
                {t("assessment_guidelines")}
              </MenuItem>
            </a> */}
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default SchoolHeader;
